'use client';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import {
  Eye,
  Download,
  MoreHorizontal,
  MoreVertical,
  RefreshCw,
  Trash2,
  FileCheck,
  FolderInput
} from 'lucide-react';
import {
  CORE_SLOT_TYPES,
  isIdentitySlotType
} from '@/lib/services/files/file-types';
import { getCoreDocumentTypeLabel } from '../utils';
import type {
  CategoriseTargetType,
  DocumentActionsVariant,
  DocumentCategory,
  IdentityAssignee
} from '../types';

interface DocumentActionsProps {
  documentId: number;
  category: DocumentCategory;
  isCertifiedCopy: boolean;
  variant?: DocumentActionsVariant;
  /**
   * The document's backend file-type slug. Only needed to decide whether the
   * identity-reassignment branch applies (VALE-806); everything else keys off
   * `category`.
   */
  typeSlug?: string;
  onView?: () => void;
  onDownload?: () => void;
  onReplace?: () => void;
  onUncategorise?: () => void;
  /** Non-core only — files the document into one of the matter's core slots (VALE-806). */
  onCategorise?: (type: CategoriseTargetType) => void;
  /** The people an identity document can be filed against. Empty hides the submenu. */
  identityAssignees?: IdentityAssignee[];
  /** Identity documents only — moves the document onto a different person (VALE-806). */
  onAssignIdentity?: (assignee: IdentityAssignee) => void;
  onDelete?: () => void;
  onToggleCertified?: () => void;
}

/**
 * DocumentActions - Action menu for documents
 *
 * Three variants:
 * - dropdown: Full dropdown menu (default)
 * - inline: Visible buttons with overflow dropdown
 * - hover-reveal: Hidden until parent hover
 */
export function DocumentActions({
  documentId: _documentId,
  category,
  isCertifiedCopy,
  variant = 'dropdown',
  typeSlug,
  onView,
  onDownload,
  onReplace,
  onUncategorise,
  onCategorise,
  identityAssignees,
  onAssignIdentity,
  onDelete,
  onToggleCertified
}: DocumentActionsProps) {
  const isCoreDocument = category === 'core';

  /**
   * The "Mark as …" and "Move ID to …" branches (VALE-806).
   *
   * This was the classic Documents tab's copy of the menu. That tab retired
   * with VALE-900 and the live menu is now `one-list/document-row-actions.tsx`;
   * this component survives **only** because a Storybook prototype
   * (`prototypes/stage-centric-design/discovery-sheet`) still mounts it. It is
   * not a shared abstraction and nothing production renders reaches it.
   */
  const categoriseItems =
    !isCoreDocument && onCategorise
      ? CORE_SLOT_TYPES.map((type) => (
          <DropdownMenuItem key={type} onClick={() => onCategorise(type)}>
            <FolderInput className="mr-2 h-4 w-4" />
            Mark as {getCoreDocumentTypeLabel(type)}
          </DropdownMenuItem>
        ))
      : null;

  const identityItems =
    onAssignIdentity &&
    typeSlug &&
    isIdentitySlotType(typeSlug) &&
    (identityAssignees?.length ?? 0) > 0 ? (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <FolderInput className="mr-2 h-4 w-4" />
          Move ID to
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          {identityAssignees?.map((assignee) => (
            <DropdownMenuItem
              key={assignee.id}
              onClick={() => onAssignIdentity(assignee)}
            >
              {assignee.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    ) : null;

  if (variant === 'inline') {
    return (
      <div className="flex items-center gap-1">
        {onView && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon-sm" onClick={onView}>
                  <Eye className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>View</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {onDownload && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon-sm" onClick={onDownload}>
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Download</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onToggleCertified && (
              <DropdownMenuItem onClick={onToggleCertified}>
                <FileCheck className="mr-2 h-4 w-4" />
                {isCertifiedCopy ? 'Remove Certification' : 'Mark as Certified'}
              </DropdownMenuItem>
            )}
            {isCoreDocument && onReplace && (
              <DropdownMenuItem onClick={onReplace}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Replace
              </DropdownMenuItem>
            )}
            {isCoreDocument && onUncategorise && (
              <DropdownMenuItem onClick={onUncategorise}>
                <FolderInput className="mr-2 h-4 w-4" />
                Uncategorise
              </DropdownMenuItem>
            )}
            {categoriseItems}
            {identityItems}
            {!isCoreDocument && onDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-error-subtle-foreground"
                  onClick={onDelete}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  if (variant === 'hover-reveal') {
    return (
      <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
        {onView && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon-xs" onClick={onView}>
                  <Eye className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>View</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {onDownload && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon-xs" onClick={onDownload}>
                  <Download className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Download</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {onToggleCertified && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  onClick={onToggleCertified}
                >
                  <FileCheck className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {isCertifiedCopy ? 'Remove Certification' : 'Mark as Certified'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    );
  }

  // Default dropdown variant
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      {/* `min-w-48` rather than a fixed `w-48`: "Mark as Grant of
          Representation" (VALE-806) wraps onto two lines inside 12rem, which reads
          as a broken menu item. The floor keeps the shorter items at their
          established width. */}
      <DropdownMenuContent align="end" className="min-w-48">
        {onView && (
          <DropdownMenuItem onClick={onView}>
            <Eye className="mr-2 h-4 w-4" />
            View
          </DropdownMenuItem>
        )}
        {onDownload && (
          <DropdownMenuItem onClick={onDownload}>
            <Download className="mr-2 h-4 w-4" />
            Download
          </DropdownMenuItem>
        )}
        {onToggleCertified && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onToggleCertified}>
              <FileCheck className="mr-2 h-4 w-4" />
              {isCertifiedCopy ? 'Remove Certification' : 'Mark as Certified'}
            </DropdownMenuItem>
          </>
        )}
        {isCoreDocument && onReplace && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onReplace}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Replace
            </DropdownMenuItem>
          </>
        )}
        {isCoreDocument && onUncategorise && (
          <DropdownMenuItem onClick={onUncategorise}>
            <FolderInput className="mr-2 h-4 w-4" />
            Uncategorise
          </DropdownMenuItem>
        )}
        {categoriseItems && (
          <>
            <DropdownMenuSeparator />
            {categoriseItems}
          </>
        )}
        {identityItems}
        {!isCoreDocument && onDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-error-subtle-foreground"
              onClick={onDelete}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
